import React, { useState, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import sxClasses from "../../flowdesigner.styles";
import DiscoverPIIPanel from "src/modules/ai-insights/discover-pii-panel/discover-pii-panel";

import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import {
    getAppModel,
    getAppStatus,
    getAppFQN,
    getNamespace,
    getNodes,
    getEdges,
    getDiscoverPIIDefaultJob
} from "../../helpers/selectors";
import {
    setIsDiscoverPIISidebarOpenAction,
    setDiscoverPIIDefaultJobAction,
    setNodesAction,
    setSelectedNodeAction,
    setIsRightSidebarOpenAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import { isUserAdmin } from "core/user-management/update.control";
import sentinelService from "src/modules/ai-insights/sentinel/sentinel-service";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import MetaObjectConverter from "core/services/metaStoreService/metaobject-converter";

export const DiscoverPIIPanelWrapper = () => {
    const dispatch = useAppDispatch();
    const isAdmin = isUserAdmin();

    const [sources, setSources] = useState<any[]>([]);
    const [sourcesReady, setSourcesReady] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);

    const appModel = useAppSelector(getAppModel);
    const appStatus = useAppSelector(getAppStatus);
    const appFQN = useAppSelector(getAppFQN);
    const namespace = useAppSelector(getNamespace);
    const nodes = useAppSelector(getNodes);
    const edges = useAppSelector(getEdges);
    const discoverPIIDefaultJob = useAppSelector(getDiscoverPIIDefaultJob);

    useEffect(() => {
        if (appModel?.getApplicationComponentsByType) {
            appModel
                .getApplicationComponentsByType("SOURCE")
                .then((sourceCollection: any) => {
                    setSources(sourceCollection?.models || []);
                    setSourcesReady(true);
                })
                .catch((error: any) => {
                    console.error("Failed to fetch sources:", error);
                    setSourcesReady(true);
                });
            return;
        }
        if (appModel) setSourcesReady(true);
    }, [appModel]);

    const handleCloseDiscoverPII = useCallback(() => {
        dispatch(setIsDiscoverPIISidebarOpenAction(false));
        dispatch(setDiscoverPIIDefaultJobAction(null));
    }, [dispatch]);

    const handleAddSentinel = useCallback(
        async (source: any, outputStreamNode: any) => {
            const entityData = await sentinelService.transformEntities(source.entities);

            // Find all components that have this stream as their source edge.
            const downstreamTargets = edges
                .filter((edge: any) => edge.source === outputStreamNode?.id)
                .map((edge: any) => edge.target)
                .filter((t: string) => t !== "temp-node");

            const newSentinelNode = {
                id: `node-sentinel-${Date.now()}`,
                type: metaStoreService.entities.SENTINEL,
                position: { x: 0, y: 0 },
                data: {
                    label: "Sentinel",
                    type: metaStoreService.entities.SENTINEL,
                    componentType: metaStoreService.entities.SENTINEL,
                    name: "Sentinel",
                    componentId: metaStoreService.entities.SENTINEL,
                    metaObjectId: "temp-node",
                    targets: downstreamTargets,
                    parentStream: outputStreamNode?.id || "",
                    targetsToBeConnectedWithSentinel: downstreamTargets,
                    metaObject: { type: metaStoreService.entities.SENTINEL },
                    entityDataFromSource: entityData
                }
            };

            dispatch(setIsDiscoverPIISidebarOpenAction(false));
            dispatch(setDiscoverPIIDefaultJobAction(null));
            dispatch(setNodesAction([...nodes, newSentinelNode]));
            dispatch(setSelectedNodeAction(newSentinelNode));
            dispatch(setIsRightSidebarOpenAction(true));
        },
        [dispatch, nodes, edges]
    );

    if (!appModel || !sourcesReady) return null;

    return (
        <Box sx={{ width: fullScreen ? "calc(100% - 69px)" : "400px" }}>
            <Box sx={[sxClasses.rightSidebarWrapper, fullScreen ? { maxWidth: "none" } : {}]} id="rightSidebarWrapper">
                <Box sx={sxClasses.piiSidebar} id="piiSidebar">
                    <DiscoverPIIPanel
                        sources={sources}
                        appName={appFQN ? MetaObjectConverter.getName(appFQN) : ""}
                        namespace={namespace || ""}
                        appFQN={appFQN ? MetaObjectConverter.convertFullNameToShortName(appFQN) : ""}
                        defaultJobSelected={discoverPIIDefaultJob ?? undefined}
                        onCancel={handleCloseDiscoverPII}
                        onSave={() => {}}
                        toggleFullScreen={() => setFullScreen(value => !value)}
                        isAdmin={isAdmin}
                        addSentinel={handleAddSentinel}
                        nodes={nodes}
                        edges={edges}
                        flowStatus={appStatus || "CREATED"}
                    />
                </Box>
            </Box>
        </Box>
    );
};
